import { BottomNavigation, BottomNavigationAction, Box, Collapse, Divider, Drawer, IconButton, List, ListItemButton, ListItemIcon, ListItemText, ListSubheader, Paper, alpha } from '@mui/material'
import {
  AccountCircle as AccountCircleIcon,
  AccountBalance as AccountBalanceIcon,
  AddBox as AddBoxIcon,
  Assessment as AssessmentIcon,
  Checklist as ChecklistIcon,
  CloseRounded as CloseRoundedIcon,
  ConstructionOutlined as ConstructionOutlinedIcon,
  CreditCard as CreditCardIcon,
  EditLocation as EditLocationIcon,
  EditNote as EditNoteIcon,
  ExpandMoreRounded as ExpandMoreRoundedIcon,
  LabelOutlined as LabelOutlinedIcon,
  LockOutlined as LockOutlinedIcon,
  MoreHorizRounded as MoreHorizRoundedIcon,
  NotificationsOutlined as NotificationsOutlinedIcon,
  PhotoCamera as PhotoCameraIcon,
  PhotoLibrary as PhotoLibraryIcon,
  Settings as SettingsIcon,
  ShoppingBasket as ShoppingBasketIcon,
  ShowChart as ShowChartIcon,
  Style as StyleIcon
} from '@mui/icons-material'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { ReactNode, SyntheticEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { NavLink, useLocation } from 'react-router'
import { useAppSelector } from '../Common/_hooks/useAppSelector'
import type UserStoreStore from '../UserData/_stores/Store/UserStoreStore'
import { STORE_ICON_URLS } from './StoreIconUrls'

type NavContext = 'account' | 'settings' | 'store'

interface MobileNavLinkItem {
  type: 'link'
  value: string
  label: string
  icon: ReactNode
  to: string
  active?: boolean
}

interface MobileNavActionItem {
  type: 'action'
  value: string
  label: string
  icon: ReactNode
  active?: boolean
}

type MobileNavItem = MobileNavLinkItem | MobileNavActionItem

const drawerListSx = {
  py: 1,
  '& a.active': {
    bgcolor: 'action.selected',
    color: 'inherit'
  }
}

const navIconSx = {
  minWidth: 40
}

export default function AccountMobileNav() {
  const [t] = useTranslation('account-v2')
  const [tR] = useTranslation('routes')
  const location = useLocation()
  const stores = useAppSelector(state => state.get('userData').get('stores'))
  const storeIntegrations = useAppSelector(state => state.get('appData').get('storeIntegrations'))
  const [moreOpened, setMoreOpened] = useState(false)
  const drawerContentRef = useRef<HTMLDivElement | null>(null)

  const settingsPath = tR('/account/settings')
  const storesPath = tR('/account/stores')

  const currentStore = useMemo(() => {
    return stores.valueSeq().find(store => {
      const storeSlug = store.get('slug')
      const storePath = tR('/account/stores/{{store}}', { store: storeSlug })
      const setupPath = tR('/account/stores/connect/{{store}}', { store: storeSlug })

      return location.pathname.indexOf(storePath) === 0
        || location.pathname === setupPath
    }) ?? null
  }, [location.pathname, stores, tR])

  const currentStoreSlug = currentStore?.get('slug') ?? null

  const navContext: NavContext = currentStoreSlug
    ? 'store'
    : (location.pathname.indexOf(settingsPath) === 0 ? 'settings' : 'account')

  const [storesOpened, setStoresOpened] = useState(navContext === 'store')
  const [settingsOpened, setSettingsOpened] = useState(navContext === 'settings')
  const [openedStoreSlug, setOpenedStoreSlug] = useState<string | null>(currentStoreSlug)

  useEffect(() => {
    if (navContext === 'store') {
      setStoresOpened(true)
      setOpenedStoreSlug(currentStoreSlug)
    }
  }, [currentStoreSlug, navContext])

  useEffect(() => {
    if (location.pathname.indexOf(storesPath) === 0) {
      setStoresOpened(true)
    }
  }, [location.pathname, storesPath])

  useEffect(() => {
    if (navContext === 'settings') {
      setSettingsOpened(true)
    }
  }, [navContext])

  const onMoreOpen = useCallback(() => {
    setMoreOpened(true)
  }, [])

  const onMoreClose = useCallback(() => {
    setMoreOpened(false)
  }, [])

  const onStoresToggle = useCallback(() => {
    setStoresOpened(value => !value)
  }, [])

  const onSettingsToggle = useCallback(() => {
    setSettingsOpened(value => !value)
  }, [])

  const onStoreToggle = useCallback((storeSlug: string) => {
    setOpenedStoreSlug(value => value === storeSlug ? null : storeSlug)
  }, [])

  const isPathActive = useCallback((path: string) => {
    return location.pathname === path
      || location.pathname.indexOf(path + '/') === 0
  }, [location.pathname])

  useEffect(() => {
    if (!moreOpened) return

    const animationFrame = window.requestAnimationFrame(() => {
      const activeItem = drawerContentRef.current?.querySelector('a.active')

      if (!(activeItem instanceof HTMLElement) || !drawerContentRef.current) return

      drawerContentRef.current.scrollTop = Math.max(
        0,
        activeItem.offsetTop - (drawerContentRef.current.clientHeight / 2) + (activeItem.clientHeight / 2)
      )
    })

    return () => window.cancelAnimationFrame(animationFrame)
  }, [location.pathname, moreOpened, openedStoreSlug, settingsOpened, storesOpened])

  const isStoreMoreActive = useMemo(() => {
    if (!currentStoreSlug) return false

    return location.pathname === tR('/account/stores/{{store}}/reports', { store: currentStoreSlug })
      || location.pathname === tR('/account/stores/{{store}}/presets', { store: currentStoreSlug })
      || location.pathname === tR('/account/stores/connect/{{store}}', { store: currentStoreSlug })
  }, [currentStoreSlug, location.pathname, tR])

  const isAccountMoreActive = useMemo(() => {
    return location.pathname === tR('/account/branding-options')
      || location.pathname === tR('/account/photo-lab')
      || location.pathname.indexOf(storesPath) === 0
  }, [location.pathname, storesPath, tR])

  const isSettingsMoreActive = useMemo(() => {
    return location.pathname === tR('/account/settings/credit-cards')
      || location.pathname === tR('/account/settings/tax-numbers')
  }, [location.pathname, tR])

  const navItems: MobileNavItem[] = useMemo(() => {
    if (navContext === 'store' && currentStoreSlug) {
      return [
        { type: 'link', value: 'store-overview', label: t('Overview'), icon: <ShowChartIcon />, to: tR('/account/stores/{{store}}/overview', { store: currentStoreSlug }), active: isPathActive(tR('/account/stores/{{store}}/overview', { store: currentStoreSlug })) },
        { type: 'link', value: 'store-products', label: t('Products'), icon: <ChecklistIcon />, to: tR('/account/stores/{{store}}/products', { store: currentStoreSlug }), active: isPathActive(tR('/account/stores/{{store}}/products', { store: currentStoreSlug })) },
        { type: 'link', value: 'store-orders', label: t('Orders'), icon: <ShoppingBasketIcon />, to: tR('/account/stores/{{store}}/orders', { store: currentStoreSlug }), active: isPathActive(tR('/account/stores/{{store}}/orders', { store: currentStoreSlug })) },
        { type: 'link', value: 'store-settings', label: t('Settings'), icon: <SettingsIcon />, to: tR('/account/stores/{{store}}/settings', { store: currentStoreSlug }), active: isPathActive(tR('/account/stores/{{store}}/settings', { store: currentStoreSlug })) },
        { type: 'action', value: 'more', label: t('More'), icon: <MoreHorizRoundedIcon />, active: isStoreMoreActive || moreOpened },
      ]
    }

    if (navContext === 'settings') {
      return [
        { type: 'link', value: 'settings-info', label: t('Info'), icon: <AccountCircleIcon />, to: tR('/account/settings/info'), active: isPathActive(tR('/account/settings/info')) },
        { type: 'link', value: 'settings-notifications', label: t('Notifications'), icon: <NotificationsOutlinedIcon />, to: tR('/account/settings/notifications'), active: isPathActive(tR('/account/settings/notifications')) },
        { type: 'link', value: 'settings-security', label: t('Security'), icon: <LockOutlinedIcon />, to: tR('/account/settings/security'), active: isPathActive(tR('/account/settings/security')) },
        { type: 'link', value: 'settings-addresses', label: t('Addresses'), icon: <EditLocationIcon />, to: tR('/account/settings/addresses'), active: isPathActive(tR('/account/settings/addresses')) },
        { type: 'action', value: 'more', label: t('More'), icon: <MoreHorizRoundedIcon />, active: isSettingsMoreActive || moreOpened },
      ]
    }

    return [
      { type: 'link', value: 'products', label: t('Products'), icon: <StyleIcon />, to: tR('/account/products'), active: isPathActive(tR('/account/products')) },
      { type: 'link', value: 'orders', label: t('Orders'), icon: <ShoppingBasketIcon />, to: tR('/account/orders'), active: isPathActive(tR('/account/orders')) },
      { type: 'link', value: 'images', label: t('Images'), icon: <PhotoLibraryIcon />, to: tR('/account/images'), active: isPathActive(tR('/account/images')) },
      { type: 'link', value: 'branding-options', label: t('Branding options'), icon: <LabelOutlinedIcon />, to: tR('/account/branding-options'), active: isPathActive(tR('/account/branding-options')) },
      { type: 'action', value: 'more', label: t('More'), icon: <MoreHorizRoundedIcon />, active: isAccountMoreActive || moreOpened },
    ]
  }, [currentStoreSlug, isAccountMoreActive, isPathActive, isSettingsMoreActive, isStoreMoreActive, moreOpened, navContext, t, tR])

  const activeBottomNavValue = useMemo(() => {
    return navItems.find(item => item.active)?.value ?? false
  }, [navItems])

  const onBottomNavChange = useCallback((_event: SyntheticEvent, value: string) => {
    const selectedItem = navItems.find(item => item.value === value)

    if (selectedItem?.type === 'action') {
      onMoreOpen()
    }
  }, [navItems, onMoreOpen])

  const renderBottomNavItem = useCallback((item: MobileNavItem) => {
    if (item.type === 'action') {
      return <BottomNavigationAction
        key={item.value}
        value={item.value}
        label={item.label}
        icon={item.icon}
      />
    }

    return <BottomNavigationAction
      key={item.value}
      component={NavLink}
      to={item.to}
      value={item.value}
      label={item.label}
      icon={item.icon}
    />
  }, [])

  const renderStoreListItem = useCallback((store: UserStoreStore) => {
    const storeSlug = store.get('slug')
    const storeName = store.get('store_name')?.trim() ?? ''
    const storeIconUrl = STORE_ICON_URLS[storeSlug]
    const storeIntegration = storeIntegrations.get(storeSlug)
    const storeOpened = openedStoreSlug === storeSlug
    const hasSetupPage = !!storeIntegration && !storeIntegration.get('legacy')

    return <Box component="div" key={store.get('id')}>
      <ListItemButton onClick={() => onStoreToggle(storeSlug)}>
        <ListItemIcon sx={navIconSx}>
          <Box
            component="img"
            src={storeIconUrl}
            alt=""
            sx={{
              width: 24,
              height: 24,
              objectFit: 'contain'
            }}
          />
        </ListItemIcon>
        <ListItemText
          sx={{ minWidth: 0, mr: 1, my: 0 }}
          secondary={storeName !== '' ? storeName : undefined}
          primaryTypographyProps={{
            sx: {
              fontWeight: storeOpened ? 600 : 400
            }
          }}
          secondaryTypographyProps={{
            noWrap: true,
            sx: {
              color: 'text.secondary',
              display: 'block',
              fontSize: '0.75rem',
              lineHeight: 1.2,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }
          }}
        >{store.get('name')}</ListItemText>
        <ExpandMoreRoundedIcon
          fontSize="large"
          sx={{
            transform: storeOpened ? 'scaleY(-1)' : 'scaleY(1)',
            transition: 'transform 250ms'
          }}
        />
      </ListItemButton>
      <Collapse in={storeOpened} timeout="auto">
        <Box component="div" sx={{ ml: 2 }}>
          <ListItemButton component={NavLink} to={tR('/account/stores/{{store}}/overview', { store: storeSlug })} onClick={onMoreClose}>
            <ListItemIcon sx={navIconSx}><ShowChartIcon /></ListItemIcon>
            <ListItemText>{t('Overview')}</ListItemText>
          </ListItemButton>
          <ListItemButton component={NavLink} to={tR('/account/stores/{{store}}/products', { store: storeSlug })} onClick={onMoreClose}>
            <ListItemIcon sx={navIconSx}><ChecklistIcon /></ListItemIcon>
            <ListItemText>{t('Products')}</ListItemText>
          </ListItemButton>
          <ListItemButton component={NavLink} to={tR('/account/stores/{{store}}/orders', { store: storeSlug })} onClick={onMoreClose}>
            <ListItemIcon sx={navIconSx}><ShoppingBasketIcon /></ListItemIcon>
            <ListItemText>{t('Orders')}</ListItemText>
          </ListItemButton>
          <ListItemButton component={NavLink} to={tR('/account/stores/{{store}}/reports', { store: storeSlug })} onClick={onMoreClose}>
            <ListItemIcon sx={navIconSx}><AssessmentIcon /></ListItemIcon>
            <ListItemText>{t('Purchase Report')}</ListItemText>
          </ListItemButton>
          <ListItemButton component={NavLink} to={tR('/account/stores/{{store}}/presets', { store: storeSlug })} onClick={onMoreClose}>
            <ListItemIcon sx={navIconSx}><EditNoteIcon /></ListItemIcon>
            <ListItemText>{t('Presets')}</ListItemText>
          </ListItemButton>
          {hasSetupPage ? <ListItemButton component={NavLink} to={tR('/account/stores/connect/{{store}}', { store: storeSlug })} onClick={onMoreClose}>
            <ListItemIcon sx={navIconSx}><ConstructionOutlinedIcon /></ListItemIcon>
            <ListItemText>{t('Setup')}</ListItemText>
          </ListItemButton> : null}
          <ListItemButton component={NavLink} to={tR('/account/stores/{{store}}/settings', { store: storeSlug })} onClick={onMoreClose}>
            <ListItemIcon sx={navIconSx}><SettingsIcon /></ListItemIcon>
            <ListItemText>{t('Settings')}</ListItemText>
          </ListItemButton>
        </Box>
      </Collapse>
    </Box>
  }, [onMoreClose, onStoreToggle, openedStoreSlug, storeIntegrations, t, tR])

  const contextLabel = navContext === 'store'
    ? t('{{store}} Store', { store: currentStore?.get('name') })
    : (navContext === 'settings' ? t('Settings') : null)

  return <>
    <Paper
      component="nav"
      elevation={8}
      square
      aria-label={t('Account navigation')}
      sx={{
        position: 'fixed',
        right: 0,
        bottom: 0,
        left: 0,
        zIndex: theme => theme.zIndex.appBar - 2,
        display: {
          xs: 'block',
          md: 'none'
        },
        maxWidth: '100vw',
        overflowX: 'hidden',
        bgcolor: 'secondary.main',
        color: 'secondary.contrastText',
        boxShadow: '0 -8px 24px rgba(0, 0, 0, 0.24)',
        pb: 'env(safe-area-inset-bottom)'
      }}
    >
      {contextLabel ? <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          minHeight: 24,
          px: 2,
          bgcolor: theme => alpha(theme.palette.primary.main, 0.14),
          color: 'secondary.contrastText',
          typography: 'caption',
          fontWeight: 700,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          textTransform: 'uppercase',
          whiteSpace: 'nowrap',
          '&::before': {
            content: '""',
            width: 6,
            height: 6,
            mr: 1,
            borderRadius: '50%',
            bgcolor: 'primary.main',
            flexShrink: 0
          }
        }}
      >
        {contextLabel}
      </Box> : null}
      <BottomNavigation
        showLabels
        value={activeBottomNavValue}
        onChange={onBottomNavChange}
        sx={{
          bgcolor: 'secondary.main',
          height: 58,
          '& .MuiBottomNavigationAction-root': {
            minWidth: 0,
            px: 0.25,
            py: 0.75,
            color: theme => alpha(theme.palette.secondary.contrastText, 0.72),
            textDecoration: 'none',
            transition: theme => theme.transitions.create('color', {
              duration: theme.transitions.duration.shorter
            }),
            '&:hover, &:focus, &:active': {
              textDecoration: 'none'
            },
            '&.Mui-selected': {
              color: 'primary.main'
            }
          },
          '& .MuiSvgIcon-root': {
            fontSize: '1.45rem',
            transition: theme => theme.transitions.create('color', {
              duration: theme.transitions.duration.shorter
            })
          },
          '& .MuiBottomNavigationAction-label': {
            mt: 0.25,
            fontSize: '0.68rem',
            lineHeight: 1.1,
            textAlign: 'center',
            textDecoration: 'none',
            transition: theme => theme.transitions.create('color', {
              duration: theme.transitions.duration.shorter
            }),
            whiteSpace: 'normal',
            '&.Mui-selected': {
              fontSize: '0.68rem'
            }
          }
        }}
      >
        {navItems.map(renderBottomNavItem)}
      </BottomNavigation>
    </Paper>
    <Drawer
      anchor="bottom"
      open={moreOpened}
      onClose={onMoreClose}
      ModalProps={{
        keepMounted: true,
        sx: {
          zIndex: 1299,
        },
      }}
      PaperProps={{
        sx: {
          maxHeight: 'calc(100dvh - 52px)',
          maxWidth: '100vw',
          overflowX: 'hidden',
          width: '100%',
        }
      }}
      sx={{
        display: {
          xs: 'block',
          md: 'none',
        }
      }}
    >
      <Box
        ref={drawerContentRef}
        component="div"
        sx={{
          position: 'relative',
          overflowY: 'auto',
          pb: 'calc(16px + env(safe-area-inset-bottom))'
        }}
      >
        <IconButton
          aria-label={t('Close')}
          onClick={onMoreClose}
          sx={{
            position: 'absolute',
            top: 8,
            right: 16,
            zIndex: 1,
          }}
        >
          <CloseRoundedIcon />
        </IconButton>
        <List
          subheader={<ListSubheader disableSticky sx={{ pr: 7 }}>{t('Account')}</ListSubheader>}
          sx={drawerListSx}
        >
          <ListItemButton component={NavLink} to={tR('/account/products')} onClick={onMoreClose}>
            <ListItemIcon sx={navIconSx}><StyleIcon /></ListItemIcon>
            <ListItemText>{t('Products')}</ListItemText>
          </ListItemButton>
          <ListItemButton component={NavLink} to={tR('/account/orders')} onClick={onMoreClose}>
            <ListItemIcon sx={navIconSx}><ShoppingBasketIcon /></ListItemIcon>
            <ListItemText>{t('Orders')}</ListItemText>
          </ListItemButton>
          <ListItemButton component={NavLink} to={tR('/account/images')} onClick={onMoreClose}>
            <ListItemIcon sx={navIconSx}><PhotoLibraryIcon /></ListItemIcon>
            <ListItemText>{t('Image Library')}</ListItemText>
          </ListItemButton>
          <ListItemButton component={NavLink} to={tR('/account/branding-options')} onClick={onMoreClose}>
            <ListItemIcon sx={navIconSx}><LabelOutlinedIcon /></ListItemIcon>
            <ListItemText>{t('Branding options')}</ListItemText>
          </ListItemButton>
          <ListItemButton component={NavLink} to={tR('/account/photo-lab')} onClick={onMoreClose}>
            <ListItemIcon sx={navIconSx}><PhotoCameraIcon /></ListItemIcon>
            <ListItemText>{t('Photo Lab')}</ListItemText>
          </ListItemButton>
        </List>
        <Divider />
        <List sx={drawerListSx}>
          <ListItemButton onClick={onStoresToggle}>
            <ListItemText primary={t('Stores')} primaryTypographyProps={{ variant: 'subtitle1' }} />
            <ExpandMoreRoundedIcon
              fontSize="large"
              sx={{
                transform: storesOpened ? 'scaleY(-1)' : 'scaleY(1)',
                transition: 'transform 250ms'
              }}
            />
          </ListItemButton>
          <Collapse in={storesOpened} timeout="auto">
            {stores.valueSeq().map(renderStoreListItem)}
            <ListItemButton component={NavLink} to={storesPath} onClick={onMoreClose} end>
              <ListItemIcon sx={navIconSx}><AddBoxIcon /></ListItemIcon>
              <ListItemText>{t('Add Store')}</ListItemText>
            </ListItemButton>
          </Collapse>
        </List>
        <Divider />
        <List sx={drawerListSx}>
          <ListItemButton onClick={onSettingsToggle}>
            <ListItemText primary={t('Settings')} primaryTypographyProps={{ variant: 'subtitle1' }} />
            <ExpandMoreRoundedIcon
              fontSize="large"
              sx={{
                transform: settingsOpened ? 'scaleY(-1)' : 'scaleY(1)',
                transition: 'transform 250ms'
              }}
            />
          </ListItemButton>
          <Collapse in={settingsOpened} timeout="auto">
            <ListItemButton component={NavLink} to={tR('/account/settings/info')} onClick={onMoreClose}>
              <ListItemIcon sx={navIconSx}><AccountCircleIcon /></ListItemIcon>
              <ListItemText>{t('Personal info')}</ListItemText>
            </ListItemButton>
            <ListItemButton component={NavLink} to={tR('/account/settings/notifications')} onClick={onMoreClose}>
              <ListItemIcon sx={navIconSx}><NotificationsOutlinedIcon /></ListItemIcon>
              <ListItemText>{t('Notifications')}</ListItemText>
            </ListItemButton>
            <ListItemButton component={NavLink} to={tR('/account/settings/security')} onClick={onMoreClose}>
              <ListItemIcon sx={navIconSx}><LockOutlinedIcon /></ListItemIcon>
              <ListItemText>{t('Security')}</ListItemText>
            </ListItemButton>
            <ListItemButton component={NavLink} to={tR('/account/settings/addresses')} onClick={onMoreClose}>
              <ListItemIcon sx={navIconSx}><EditLocationIcon /></ListItemIcon>
              <ListItemText>{t('Addresses')}</ListItemText>
            </ListItemButton>
            <ListItemButton component={NavLink} to={tR('/account/settings/credit-cards')} onClick={onMoreClose}>
              <ListItemIcon sx={navIconSx}><CreditCardIcon /></ListItemIcon>
              <ListItemText>{t('Credit cards')}</ListItemText>
            </ListItemButton>
            <ListItemButton component={NavLink} to={tR('/account/settings/tax-numbers')} onClick={onMoreClose}>
              <ListItemIcon sx={navIconSx}><AccountBalanceIcon /></ListItemIcon>
              <ListItemText>{t('Tax numbers')}</ListItemText>
            </ListItemButton>
          </Collapse>
        </List>
      </Box>
    </Drawer>
  </>
}
